import { useState, useEffect } from 'react';

import {
  assignProductToClient,
  getClientProducts,
  unassignProductFromClient,
} from 'api/generated/client-products/client-products';
import { getReports } from 'api/generated/report-controller/report-controller';

import RadioInput from 'components/partials/radio-input/radio-input';
import Heading from 'components/partials/heading/heading';
import TableComponent from 'components/partials/table-component/table-component';
import EmptySpaceContent from 'components/partials/empty-space-content/empty-space-content';
import Loader from 'components/partials/loader/loader';
import { getClientProductsTableSettings } from 'containers/admin/clients/client/tabs-components/products-tab/products-tab-table-settings';

import { useProductsList } from 'hooks/api/use-products';
import { useCurrentUser } from 'hooks/api/use-current-user';

import type { SortingState } from '@tanstack/react-table';
import type {
  ClientProductRow,
  ClientReportRow,
} from 'containers/admin/clients/client/tabs-components/products-tab/products-tab-table-settings';

interface ProductsTabProps {
  clientId: string | number | undefined;
}

interface RadioOption {
  label: string;
  value: 'assigned' | 'all';
  colorClass?: string;
}

const radioOptions: RadioOption[] = [
  { label: 'Assigned', value: 'assigned' },
  { label: 'All available', value: 'all' },
];

const ProductsTab = ({ clientId }: ProductsTabProps): React.JSX.Element => {
  const currentUser = useCurrentUser();
  const currentUserData = currentUser?.data as
    | {
        oktaUserId?: string;
        internalAdmin?: boolean;
        reportAdmin?: boolean;
      }
    | null
    | undefined;
  const oktaUserId = currentUserData?.oktaUserId ?? '';
  const isInternalAdmin =
    !!currentUserData?.internalAdmin && !currentUserData?.reportAdmin;

  const [isClientDataLoading, setIsClientDataLoading] =
    useState<boolean>(false);
  const [isToggling, setIsToggling] = useState<boolean>(false);
  const [filterOption, setFilterOption] = useState<'assigned' | 'all'>(
    'assigned'
  );
  const [clientProducts, setClientProducts] = useState<ClientProductRow[]>([]);
  const [reports, setReports] = useState<ClientReportRow[]>([]);
  const [initialLoading, setInitialLoading] = useState<boolean>(true);

  const [sortingParams, setSortingParams] = useState<SortingState>([
    { id: 'name', desc: false },
  ]);

  const {
    data: allProductsResponse,
    isLoading: isAllProductsLoading,
    isError: isAllProductsError,
  } = useProductsList({
    pageSize: 100,
    sortBy: sortingParams,
    filters: clientId ? { clientId } : null,
    enabled: filterOption === 'all',
  });

  const allProducts = (allProductsResponse?.data ?? []) as ClientProductRow[];

  const handleToggleChange = async (
    product: ClientProductRow,
    newToggleValue: boolean
  ): Promise<void> => {
    if (isInternalAdmin || !clientId) return;

    setIsToggling(true);
    try {
      if (newToggleValue) {
        await assignProductToClient(
          oktaUserId,
          Number(clientId),
          Number(product.id)
        );
      } else {
        await unassignProductFromClient(
          oktaUserId,
          Number(clientId),
          Number(product.id)
        );
      }

      await fetchClientData();
    } catch (error) {
      console.error('Error during product assignment toggle', error);
    } finally {
      setIsToggling(false);
    }
  };

  const columns = getClientProductsTableSettings(
    clientId,
    reports,
    clientProducts,
    handleToggleChange,
    isInternalAdmin
  );

  const fetchClientData = async (): Promise<void> => {
    if (!clientId) return;
    setIsClientDataLoading(true);
    try {
      const [clientProductsList, reportsResponse] = await Promise.all([
        getClientProducts(oktaUserId, Number(clientId), {
          size: 1000,
          sort: [
            `${sortingParams[0].id},${sortingParams[0].desc ? 'desc' : 'asc'}`,
          ],
        }),
        getReports(oktaUserId),
      ]);

      if (clientProductsList) {
        setClientProducts(
          clientProductsList.map((row) => ({
            ...row,
            id: row.id ?? 0,
          }))
        );
      }
      if (Array.isArray(reportsResponse.data)) {
        setReports(reportsResponse.data as ClientReportRow[]);
      }
    } catch (error) {
      console.error('Error fetching reports/client products', error);
    } finally {
      setIsClientDataLoading(false);

      if (initialLoading) {
        setInitialLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchClientData();
  }, [sortingParams]);

  const isLoading =
    filterOption === 'assigned' ? isClientDataLoading : isAllProductsLoading;

  const isError = filterOption === 'all' && isAllProductsError;

  const dataToShow: ClientProductRow[] =
    filterOption === 'assigned'
      ? clientProducts
      : allProducts.map((product) => {
          const assignedProduct = clientProducts.find(
            (p) => p.id === product.id
          );

          return assignedProduct
            ? { ...product, assignedDate: assignedProduct.assignedDate }
            : product;
        });

  return (
    <div className="relative flex flex-1 flex-col">
      <Loader
        loading={initialLoading}
        customClass="!bg-white"
        fullScreen={false}
        overlayClassName="!opacity-0"
      />
      <div className="flex justify-between items-center my-5">
        <Heading variant="h2">Products</Heading>
      </div>
      {!isInternalAdmin && (
        <div className="flex gap-5">
          <div className="font-semibold text-gray-600">Show</div>
          <div className="flex">
            {radioOptions.map((option) => (
              <RadioInput<'assigned' | 'all'>
                key={option.value}
                labelFor={option.value}
                label={option.label}
                radioValue={option.value}
                setAction={(val) => val && setFilterOption(val)}
                onChange={(val) => val && setFilterOption(val)}
                value={filterOption}
                customClass="mr-4"
                labelClass={option.colorClass}
              />
            ))}
          </div>
        </div>
      )}
      <div className="relative flex flex-1 flex-col">
        {(!isError && !!dataToShow.length) || (isLoading && !initialLoading) ? (
          <TableComponent
            columns={columns}
            data={dataToShow}
            isLoading={isLoading || isToggling}
            sortingParams={sortingParams}
            setSortingParams={setSortingParams}
            customHeight="max-h-[50vh]"
            pagination={false}
          />
        ) : !isLoading && !dataToShow.length ? (
          <EmptySpaceContent
            customClass="flex-1"
            text="There are no files to show yet."
          />
        ) : null}
      </div>
    </div>
  );
};

export default ProductsTab;
